<template>
  <!-- Static version (non-interactive) -->
  <div v-if="static" class="flex text-xs items-center relative flex-wrap cursor-default">
    <span
      :title="statusDisplay()"
      class="px-4 rounded-full test-xs min-w-64 line-clamp-2 py-1 flex justify-center items-center max-w-64 text-pretty min-h-10 text-center"
      :style="{ backgroundColor: statusData?.color, color: statusData?.text }"
    >
      {{ statusDisplay() }}
    </span>
  </div>

  <!-- Interactive version with shadcn-vue Select -->
  <div v-else class="relative">
    <Select
      :model-value="currentStatus"
      @update:model-value="(value) => value && handleStatusChange(value as string)"
    >
      <SelectTrigger
        class="w-fit pl-2 pr-4 rounded-full hover:outline hover:outline-4 hover:outline-gray-100 border-0 shadow-none flex items-center min-w-64 min-h-10 max-w-64 text-pretty"
        :style="{ backgroundColor: statusData?.color || '#f0f0f0', color: statusData?.text }"
      >
        <div class="flex items-center w-full">
          <span v-if="isLoading" class="w-56 flex justify-center items-center h-5 py-1">
            <Loader2 class="w-4 h-4 ml-1 animate-spin" />
          </span>
          <span v-else class="text-xs py-1 text-center w-full h-full text-pretty truncate">
            {{ statusDisplay() }}
          </span>
        </div>
      </SelectTrigger>

      <SelectContent class="w-80 z-[9999]">
        <SelectGroup>
          <SelectItem
            v-for="step in filteredApplicationSteps"
            :key="step.id"
            :value="step.id"
            class="p-0 focus:bg-transparent focus:text-inherit hover:bg-transparent data-[highlighted]:bg-transparent data-[highlighted]:text-inherit"
          >
            <button
              class="text-left px-2 rounded-full hover:outline hover:outline-4 hover:outline-gray-100 cursor-pointer text-xs py-1 w-full my-1 flex items-center justify-between"
              :style="{ backgroundColor: step.color, color: step.text }"
            >
              <span v-if="!isCommon">{{ step.id }} - {{ step.status }}</span>
              <span class="w-full" v-else>{{ step.common }}</span>
              <Loader2 v-if="loadingStatus === step.id" class="w-4 h-4 animate-spin ml-2" />
            </button>
          </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  </div>
</template>

<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next'
import applicationSteps from '@/const/applicationSteps'
import { ref, computed, watch } from 'vue'
import { useApplicationsStore } from '@/stores/applications'
import { useToast } from '@/components/ui/toast'
import { useAuth } from '@/composables/useAuth'

// Import shadcn-vue Select components
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger
} from '@/components/ui/select'

const { isGuide, isCommon } = useAuth()

const { toast } = useToast()

const props = defineProps<{
  currentStatus: string
  applicationId: string | number
  nolabel?: boolean
  static?: boolean
  isOpen?: boolean
}>()

const emit = defineEmits<{
  'update:currentStatus': [status: string]
  toggle: [isOpen: boolean]
}>()

const applicationsStore = useApplicationsStore()
const isLoading = ref(false)
const loadingStatus = ref<string | null>(null)

const statusData = computed(() => {
  return applicationSteps.find((step) => step.id === props.currentStatus)
})

// Filter application steps based on user role
const filteredApplicationSteps = computed(() => {
  if (isCommon.value) {
    const commonSteps = applicationSteps.filter((step) => step.common !== undefined)
    const index = commonSteps.findIndex(element => element.common === "Rejected")
    if (index !== -1) {
      const rejectedElement = commonSteps.splice(index, 1)[0]
      commonSteps.push(rejectedElement)
    }
    return commonSteps
  } else {
    return applicationSteps.filter((step) => (isGuide.value ? true : !step.restricted))
  }
})

function statusDisplay() {
  if (!statusData.value) return 'No status'
  if (isCommon.value) return statusData.value.common
  return `${statusData.value.id} - ${statusData.value.status}`
}

const handleStatusChange = async (stepId: string) => {
  if (stepId === props.currentStatus) return

  // Find the step data
  const step = applicationSteps.find((s) => s.id === stepId)
  if (!step) return

  loadingStatus.value = stepId
  isLoading.value = true

  try {
    await applicationsStore.updateApplicationStatus(props.applicationId.toString(), stepId)
    // Wait for a moment to show the updated status
    await new Promise((resolve) => setTimeout(resolve, 500))

    toast({
      title: 'Application status updated',
      description: `Status updated to "${step.status}"`,
      variant: 'default'
    })

    emit('update:currentStatus', stepId)
    emit('toggle', false)
  } catch (error: any) {
    toast({
      title: 'Failed to update application status',
      description: error.response?.data?.detail || 'An error occurred',
      variant: 'destructive'
    })
  } finally {
    loadingStatus.value = null
    isLoading.value = false
  }
}

// Handle open state from parent
watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue !== undefined) {
      emit('toggle', newValue)
    }
  }
)
</script>

<style scoped>
/* Ensure our custom styles override shadcn defaults */
:deep(.select-trigger) {
  box-shadow: none;
  border-color: transparent;
}

:deep(.select-content) {
  max-height: 16rem;
  overflow-y: auto;
}
</style>
